/**
 * <ll-collapsible-text> — text clamped to a few lines that expands when tapped.
 *
 * The clamp itself is CSS, keyed on `[data-clamped]:not([data-expanded])` (the host section
 * owns the line count and the fade). This element only decides whether clamping is needed:
 * it measures with the clamp applied, and keeps `data-clamped` only if the text overflows.
 * When it does, the element becomes a button (role, tabindex, aria-expanded) that toggles
 * `data-expanded` on click, Enter and Space. When the text fits, none of that is added.
 *
 * Used by sections/ll-brand-collection-intro.liquid.
 */
class LlCollapsibleText extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#toggle);
    this.addEventListener('keydown', this.#onKeydown);
    this.#observer = new ResizeObserver(() => this.#measure());
    this.#observer.observe(this);
    this.#measure();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#toggle);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#observer?.disconnect();
  }

  /** @type {ResizeObserver | undefined} */
  #observer;

  #measure() {
    // Measuring an expanded text would always say „fits"; the reader chose to open it.
    if (this.hasAttribute('data-expanded')) return;

    this.setAttribute('data-clamped', '');
    const overflows = this.scrollHeight > this.clientHeight + 1;

    if (overflows) {
      this.setAttribute('role', 'button');
      this.setAttribute('tabindex', '0');
      this.setAttribute('aria-expanded', 'false');
    } else {
      this.removeAttribute('data-clamped');
      this.removeAttribute('role');
      this.removeAttribute('tabindex');
      this.removeAttribute('aria-expanded');
    }
  }

  #toggle = () => {
    if (!this.hasAttribute('data-clamped')) return;
    // Selecting text inside the summary should not collapse it.
    if (window.getSelection()?.toString()) return;

    const expanded = !this.hasAttribute('data-expanded');
    this.toggleAttribute('data-expanded', expanded);
    this.setAttribute('aria-expanded', String(expanded));
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.#toggle();
  };
}

if (!customElements.get('ll-collapsible-text')) {
  customElements.define('ll-collapsible-text', LlCollapsibleText);
}
